// clustering.ts
export type DataPoint = {
  id: string;
  x: number;
  y: number;
  image: string;
  [key: string]: string | number | undefined; // Allow other properties
};

export type ClusterPoint = {
  id: string;
  x: number;
  y: number;
  image: string;
  items: DataPoint[];
  isCluster: boolean;
  covered_topics?: number;
  total_material_topics?: number;
};

/**
 * Calculate Euclidean distance between two points in normalized space
 */
function calculateDistance(
  point1: { x: number; y: number },
  point2: { x: number; y: number },
  xRange: number,
  yRange: number
): number {
  // Normalize the coordinates to account for different scales
  const normalizedX1 = point1.x / xRange;
  const normalizedY1 = point1.y / yRange;
  const normalizedX2 = point2.x / xRange;
  const normalizedY2 = point2.y / yRange;

  return Math.sqrt(
    Math.pow(normalizedX2 - normalizedX1, 2) +
      Math.pow(normalizedY2 - normalizedY1, 2)
  );
}

/**
 * Dynamically cluster points that are too close together
 */
export function clusterPoints(
  data: DataPoint[],
  minDistance = 0.05 // Minimum normalized distance between points
): ClusterPoint[] {
  if (data.length === 0) return [];

  // Calculate data ranges for normalization
  const xValues = data.map((d) => d.x);
  const yValues = data.map((d) => d.y);
  const xRange = Math.max(...xValues) - Math.min(...xValues) || 1;
  const yRange = Math.max(...yValues) - Math.min(...yValues) || 1;

  // Start with all points as potential clusters
  const clusters: ClusterPoint[] = data.map((point) => ({
    ...point,
    items: [point],
    isCluster: false,
  }));

  let merged = true;

  // Keep merging until no more merges are possible
  while (merged) {
    merged = false;

    for (let i = 0; i < clusters.length - 1; i++) {
      for (let j = i + 1; j < clusters.length; j++) {
        const distance = calculateDistance(
          clusters[i],
          clusters[j],
          xRange,
          yRange
        );

        if (distance < minDistance) {
          // Merge cluster j into cluster i
          const mergedItems = [...clusters[i].items, ...clusters[j].items];

          // Calculate weighted centroid
          const totalWeight = mergedItems.length;
          const centroidX =
            mergedItems.reduce((sum, item) => sum + item.x, 0) / totalWeight;
          const centroidY =
            mergedItems.reduce((sum, item) => sum + item.y, 0) / totalWeight;

          // Update cluster i
          clusters[i] = {
            id: `cluster-${clusters[i].id}-${clusters[j].id}`,
            x: centroidX,
            y: centroidY,
            image: clusters[i].image, // Keep first image as representative
            items: mergedItems,
            isCluster: mergedItems.length > 1,
          };

          // Remove cluster j
          clusters.splice(j, 1);
          merged = true;
          break;
        }
      }
      if (merged) break;
    }
  }

  return clusters;
}

/**
 * Get optimal cluster distance based on data density
 */
export function getOptimalClusterDistance(
  data: DataPoint[],
  targetMaxClusters = 50
): number {
  if (data.length <= targetMaxClusters) {
    return 0; // No clustering needed
  }

  // Start with a small distance and increase until we get acceptable number of clusters
  let distance = 0.02;
  let clustered = clusterPoints(data, distance);

  while (clustered.length > targetMaxClusters && distance < 0.3) {
    distance += 0.01;
    clustered = clusterPoints(data, distance);
  }

  return distance;
}

/**
 * Adaptive clustering that automatically determines optimal distance
 */
export function adaptiveCluster(
  data: DataPoint[],
  maxClusters = 50
): ClusterPoint[] {
  const optimalDistance = getOptimalClusterDistance(data, maxClusters);
  return clusterPoints(data, optimalDistance);
}
